import { createAsyncThunk } from "@reduxjs/toolkit";
import { PartiallyRequired } from "@regal-voice/shared-types";
import omit from "lodash/omit";

import { apolloQuery } from "Services/ApolloClientService";
import { getProfilesById } from "Services/marketing-api/profiles/queries";

import { clearManyContacts, setContactAction, upsertContactsAction } from "./ContactsStateProvider";
import { contactsSelectors, selectContactByIdOrPhone, selectContactsByPhone } from "./Selectors/Selectors";
import { createContactFromPartial, mergeContact } from "./utils";

import type { RVDispatch } from "../Storage";
import type { Profile } from "Types/Profile";

const clearContactsByPhone = createAsyncThunk<void, string, { state: RootState }>(
    "contactsState/clearContactByPhone",
    (contactPhone, { dispatch, getState }) => {
        const existingContacts = selectContactsByPhone(getState(), contactPhone);
        if (existingContacts.length) {
            dispatch(clearManyContacts(existingContacts.map((c) => c.id)));
        }
    }
);

const clearContactsByPhoneList = createAsyncThunk<void, string[], { state: RootState }>(
    "contactsState/clearContactsByPhoneList",
    (contactPhones, { dispatch, getState }) => {
        const allContacts = contactsSelectors.selectAll(getState());
        const foundContacts = allContacts.filter(
            (c) =>
                (c.contactPhone && contactPhones.includes(c.contactPhone)) ||
                c.phones?.map(({ phoneNumber }) => phoneNumber).some((phone) => contactPhones.includes(phone))
        );
        if (foundContacts.length) {
            dispatch(clearManyContacts(foundContacts.map((c) => c.id)));
        }
    }
);

export const setContact = createAsyncThunk<void, Profile, { state: RootState; dispatch: RVDispatch }>(
    "contactsState/setContact",
    (contact, { dispatch }) => {
        if (contact) {
            // remove existing contact with given phone
            const { contactPhone } = contact;
            if (contactPhone) {
                dispatch(clearContactsByPhone(contactPhone));
            }
            // remove existing contacts with any of this contact's phones. this is a double check, it shouldn't be needed
            const allPhones = contact.phones?.map(({ phoneNumber }) => phoneNumber);
            if (allPhones?.length) {
                dispatch(clearContactsByPhoneList(allPhones));
            }
            dispatch(setContactAction(contact));
        }
    }
);

export const upsertContacts = createAsyncThunk<void, Profile[], { state: RootState; dispatch: RVDispatch }>(
    "contactsState/upsertContacts",
    (contacts, { dispatch }) => {
        // remove existing contacts
        const contactPhones = contacts.reduce<string[]>((acc, contact) => {
            if (contact.contactPhone) {
                acc.push(contact.contactPhone);
            }
            return acc;
        }, []);
        dispatch(clearContactsByPhoneList(contactPhones));
        if (contacts) {
            dispatch(upsertContactsAction(contacts));
        }
    }
);

// We're reviving this prefetching contacts experiment but only for profiles to reduce risk and complexity
export const fetchContactList = createAsyncThunk<
    void,
    string[],
    {
        dispatch: RVDispatch;
        state: RootState;
    }
>("contacts/fetch", async (profileIds: string[], { dispatch }) => {
    const { data } = await apolloQuery({
        query: getProfilesById,
        variables: { profileIds },
    });
    const profiles = data.getProfilesById;

    if (profiles.length) {
        dispatch(upsertContacts(profiles));
    }
});

export const updateContact = createAsyncThunk<
    void,
    Partial<Profile>,
    {
        dispatch: RVDispatch;
        state: RootState;
    }
>("contacts/update", (data: Partial<Profile>, { dispatch, getState }) => {
    if (!data?.id && !data?.contactPhone) {
        return;
    }

    const contact = selectContactByIdOrPhone(getState() as RootState, (data.id || data.contactPhone) as string);
    if (contact) {
        const contactUpdated = mergeContact(contact, omit(data, "contactPhone"));
        dispatch(setContact(contactUpdated));
    }
});

/**
 * Given a list of partial contacts, shalowly updates all existing contacts
 * and adds all others using fallback/default data to flesh out the full Contacts
 */
export const addPartialContacts = createAsyncThunk<
    void,
    PartiallyRequired<Profile, "id">[],
    {
        dispatch: RVDispatch;
        state: RootState;
    }
>("contacts/addPartialContacts", (contacts: PartiallyRequired<Profile, "id">[], { getState, dispatch }) => {
    const rootState = getState() as RootState;
    const existingEntities = contactsSelectors.selectEntities(rootState);
    const fullContacts = contacts.map((partialContact) => {
        const partialContactId = partialContact.id;
        const existingContact = partialContactId && existingEntities[partialContactId];
        if (existingContact) {
            return { ...existingContact, ...partialContact };
        } else {
            return createContactFromPartial(partialContact);
        }
    });
    dispatch(upsertContacts(fullContacts));
});
